import { create } from 'zustand'
import type { RecentDose, PetDashboardSummary } from '@rondela/shared'
import { api, ApiError } from '../../shared/lib/api'

export type DashboardSortOption = 'urgency' | 'medications' | 'appointments' | 'alphabetical'

interface DashboardState {
  summaries: PetDashboardSummary[]
  recentDoses: RecentDose[]
  sortOption: DashboardSortOption
  isLoading: boolean
  error: string | null

  // Actions
  fetchDashboardSummaries: () => Promise<void>
  fetchRecentDoses: () => Promise<void>
  setSortOption: (option: DashboardSortOption) => void
  clearError: () => void
}

/**
 * Sort summaries based on the selected option
 * Backend returns sorted by urgency, but we may need to re-sort client-side for other options
 */
function sortSummaries(summaries: PetDashboardSummary[], option: DashboardSortOption): PetDashboardSummary[] {
  const sorted = [...summaries]
  switch (option) {
    case 'urgency':
      sorted.sort((a, b) => b.urgencyScore - a.urgencyScore)
      break
    case 'medications':
      sorted.sort((a, b) => b.pendingDosesCount - a.pendingDosesCount)
      break
    case 'appointments':
      sorted.sort((a, b) => {
        const aDate = a.nextAppointment?.date || '9999-12-31'
        const bDate = b.nextAppointment?.date || '9999-12-31'
        return aDate.localeCompare(bDate)
      })
      break
    case 'alphabetical':
      sorted.sort((a, b) => a.nombre.localeCompare(b.nombre))
      break
  }
  return sorted
}

export const useDashboardStore = create<DashboardState>((set, get) => ({
  summaries: [],
  recentDoses: [],
  sortOption: 'urgency',
  isLoading: false,
  error: null,

  fetchDashboardSummaries: async () => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.get<{ summaries: PetDashboardSummary[] }>('/api/pets/dashboard-summaries')
      const sorted = sortSummaries(response.summaries, get().sortOption)
      set({ summaries: sorted, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al cargar resumen del dashboard'
      set({ error: message, isLoading: false })
    }
  },

  fetchRecentDoses: async () => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.get<{ doses: RecentDose[] }>('/api/doses/recent')
      set({ recentDoses: response.doses, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al cargar dosis recientes'
      set({ error: message, isLoading: false })
    }
  },

  setSortOption: (option: DashboardSortOption) => {
    const sorted = sortSummaries(get().summaries, option)
    set({ sortOption: option, summaries: sorted })
  },

  clearError: () => set({ error: null }),
}))
